import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Users, BookOpen, Shield, ArrowRight, Brain, Calendar, Tag, Sparkles, Globe, GraduationCap, ChevronDown, Cpu, Eye, Scale, FileText, Clock, MapPin, Megaphone, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProspectiveStudentsModal from '../components/ProspectiveStudentsModal';

interface NewsItem {
  id: number;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  image: string;
  tags: string[];
}

interface ColumnItem {
  id: number;
  title: string;
  subtitle: string;
  content: string;
  author: {
    name: string;
    title: string;
    affiliation: string;
    bio: string;
    image: string;
  };
  date: string;
  category: string;
  tags: string[];
  featured: boolean;
}

// ── 타이핑 인디케이터 ────────────────────────────────────
const TypingDots: React.FC = () => (
  <div className="flex items-center space-x-1 px-4 py-3">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="w-2 h-2 bg-teal-400 rounded-full inline-block animate-bounce"
        style={{ animationDelay: `${i * 150}ms` }}
      />
    ))}
  </div>
);

// ── 메시지 버블 ─────────────────────────────────────────
interface BubbleProps {
  visible: boolean;
  side: 'human' | 'ai';
  label: string;
  english?: React.ReactNode;
  children: React.ReactNode;
}

const Bubble: React.FC<BubbleProps> = ({ visible, side, label, english, children }) => (
  <div
    className={`flex ${side === 'human' ? 'justify-end' : 'justify-start'} transition-all duration-700 ease-out ${
      visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
    }`}
  >
    <div className="max-w-[85%] md:max-w-[75%]">
      <div className={`text-xs font-semibold mb-1.5 ${side === 'human' ? 'text-right text-amber-400' : 'text-teal-400'}`}>
        {label}
      </div>
      <div
        className={`rounded-2xl px-5 py-4 text-sm leading-relaxed ${
          side === 'human'
            ? 'bg-amber-500/15 border border-amber-500/20 text-amber-50 rounded-tr-sm'
            : 'bg-teal-500/15 border border-teal-500/20 text-teal-50 rounded-tl-sm'
        }`}
      >
        {children}
        {english && (
          <p className={`mt-3 pt-3 text-xs leading-relaxed border-t ${
            side === 'human' ? 'border-amber-500/15 text-amber-200/40' : 'border-teal-500/15 text-teal-300/40'
          }`}>
            {english}
          </p>
        )}
      </div>
    </div>
  </div>
);

// ── 카운트업 애니메이션 ──────────────────────────────────
const CountUp: React.FC<{ end: number; suffix?: string; duration?: number }> = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let startTime: number;
    const animate = (time: number) => {
      if (!startTime) startTime = time;
      const progress = Math.min((time - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [started, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// ── 홈 페이지 ────────────────────────────────────────────
const HomePage: React.FC = () => {
  const [recentNews, setRecentNews] = useState<NewsItem[]>([]);
  const [recentColumns, setRecentColumns] = useState<ColumnItem[]>([]);
  const [step, setStep] = useState<number>(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // 신입생 안내 팝업
  const [showPopup, setShowPopup] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const hideUntil = Number(localStorage.getItem('chair_prospective_hide_until') || 0);
    if (Date.now() > hideUntil) {
      const t = setTimeout(() => setShowPopup(true), 1500);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    fetch('/api/news')
      .then((r) => r.json())
      .then((data: NewsItem[]) => setRecentNews(data.slice(0, 3)))
      .catch(() => setRecentNews([]));

    fetch('/api/columns')
      .then((r) => r.json())
      .then((data: ColumnItem[]) => {
        const sorted = [...data].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setRecentColumns(sorted.slice(0, 2));
      })
      .catch(() => setRecentColumns([]));
  }, []);

  const dismissPopupToday = () => {
    const expiry = new Date();
    expiry.setHours(24, 0, 0, 0);
    localStorage.setItem('chair_prospective_hide_until', String(expiry.getTime()));
    setShowPopup(false);
  };

  const openModalFromPopup = () => {
    setShowPopup(false);
    setShowModal(true);
  };

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !startedRef.current) {
          startedRef.current = true;
          const delays = [300, 1200, 2800, 4000, 5200, 6400, 8200, 9600];
          delays.forEach((delay, i) => {
            const t = setTimeout(() => setStep(i + 1), delay);
            timersRef.current.push(t);
          });
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(el);
    const timers = timersRef.current;
    return () => {
      observer.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });

  // IWD 2026 D-day 계산
  const eventDate = new Date('2026-03-27T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dDay = Math.ceil((eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const dDayLabel = dDay > 0 ? `D-${dDay}` : dDay === 0 ? 'D-Day' : '종료';

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* 신입생 안내 플로팅 팝업 카드 */}
      <ProspectiveStudentsModal open={showModal} onClose={() => setShowModal(false)} />
      {showPopup && (
        <div className="fixed top-20 right-5 z-[90] max-w-sm animate-[slideDown_0.4s_ease-out]">
          <div className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 rounded-2xl p-[2px] shadow-2xl">
            <button
              onClick={() => setShowPopup(false)}
              aria-label="닫기"
              className="absolute -top-2 -right-2 z-10 bg-white text-gray-700 rounded-full p-1.5 shadow-md hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
            <button
              onClick={openModalFromPopup}
              className="text-left w-full bg-white rounded-[14px] p-5 hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 bg-gradient-to-br from-indigo-500 to-purple-600 w-11 h-11 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <GraduationCap className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="inline-flex items-center gap-1 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 px-2 py-0.5 rounded-full text-[10px] font-semibold mb-1.5">
                    <Sparkles className="h-2.5 w-2.5" />
                    FOR NEWCOMERS
                  </div>
                  <h4 className="font-bold text-gray-900 text-sm mb-1 group-hover:text-indigo-600 transition-colors">
                    신입생 여러분에게
                  </h4>
                  <p className="text-gray-600 text-xs leading-relaxed mb-2">
                    AI 시대, 어떤 대학을 선택해야 할까?<br/>
                    CHAIR가 들려주는 이야기 →
                  </p>
                </div>
              </div>
            </button>
            <div className="px-5 pb-3 bg-white rounded-b-[14px] -mt-1">
              <button
                onClick={dismissPopupToday}
                className="text-[11px] text-gray-400 hover:text-gray-600 underline underline-offset-2"
              >
                오늘 하루 보지 않기
              </button>
            </div>
          </div>
        </div>
      )}


      {/* ══════════════════════════════════════════════════════
          HERO — 풀스크린 + 파티클 + 상단 알림 띠
      ══════════════════════════════════════════════════════ */}
      <section className="hero-fullscreen">
        {/* 파티클 배경 — 인라인 애니메이션 */}
        <div className="absolute w-[400px] h-[400px] rounded-full top-[10%] right-[-5%] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(44,188,179,0.08) 0%, transparent 70%)', animation: 'float-slow 20s ease-in-out infinite' }} />
        <div className="absolute w-[300px] h-[300px] rounded-full bottom-[15%] left-[-3%] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(27,58,92,0.15) 0%, transparent 70%)', animation: 'float-medium 15s ease-in-out infinite' }} />
        <div className="absolute w-[200px] h-[200px] rounded-full top-[50%] left-[40%] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(44,188,179,0.06) 0%, transparent 70%)', animation: 'pulse-glow 8s ease-in-out infinite' }} />
        <div className="absolute w-[150px] h-[150px] rounded-full top-[20%] left-[15%] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(44,188,179,0.05) 0%, transparent 70%)', animation: 'float-slow 25s ease-in-out infinite reverse' }} />
        <div className="absolute inset-0 grid-pattern opacity-20" />

        {/* 알림 띠 — 히어로 내부 상단 */}
        <div className="absolute top-16 sm:top-18 left-0 right-0 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-flex flex-wrap items-center gap-3 sm:gap-5 bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-4 py-2 text-xs sm:text-sm">
              {/* 새 컬럼 알림 */}
              <Link to="/column" className="flex items-center gap-2 text-white/90 hover:text-teal-300 transition-colors whitespace-nowrap group">
                <span className="inline-flex items-center bg-teal-400 text-navy-900 font-bold px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider animate-pulse">
                  New
                </span>
                <span className="font-medium truncate max-w-[250px] sm:max-w-none">
                  {recentColumns.length > 0
                    ? `${recentColumns[0].author.name} — ${recentColumns[0].title}`
                    : '고상원 — 인간유사 AI에서 인간중심 AI로의 전환'}
                </span>
                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>

              <span className="w-px h-4 bg-white/20 hidden sm:block" />

              {/* 예정 행사 D-day */}
              {dDay >= 0 && (
                <Link to="/ai4her-iwd2026" className="flex items-center gap-2 text-white/90 hover:text-amber-300 transition-colors whitespace-nowrap group">
                  <span className="inline-flex items-center bg-amber-400 text-navy-900 font-bold px-2 py-0.5 rounded-full text-[10px] animate-pulse">
                    {dDayLabel}
                  </span>
                  <span className="font-medium hidden sm:inline">IWD 2026 Forum</span>
                  <span className="font-medium sm:hidden">IWD 2026</span>
                  <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-32 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* 왼쪽: 텍스트 */}
            <div>
              <div className="inline-flex items-center bg-white/8 border border-white/12 rounded-full px-4 py-1.5 text-xs font-medium text-teal-300 mb-8 backdrop-blur-sm">
                <Sparkles className="h-3.5 w-3.5 mr-2" />
                Seoul Women's University Research Center
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-[1.1] tracking-tight">
                AI 시대,
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-teal-300">
                  인간의 속도
                </span>
                <span className="text-white">로</span>
                <br />
                나아가다
              </h1>

              <p className="text-lg md:text-xl text-navy-200/80 mb-5 leading-relaxed max-w-xl">
                협력은 아름다운 말입니다. 그러나 진정한 협력은
                <strong className="text-white font-semibold"> 충분한 앎(awareness)</strong> 위에서 시작됩니다.
              </p>

              <p className="text-base text-navy-300/60 max-w-lg leading-relaxed mb-10">
                인간-AI 협력 연구센터(CHAIR)는 AI 기술이 무엇을 수집하고,
                어떻게 작동하며, 우리 사회에 어떤 영향을 미치는지를
                먼저 이해하는 것에서 출발합니다.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center px-7 py-3.5 bg-teal-500 text-white text-sm font-semibold rounded-xl hover:bg-teal-400 transition-all duration-300 shadow-lg shadow-teal-500/25 hover:shadow-teal-400/40 hover:-translate-y-0.5"
                >
                  센터 소개
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link
                  to="/philosophy"
                  className="inline-flex items-center justify-center px-7 py-3.5 bg-white/8 text-white text-sm font-semibold rounded-xl hover:bg-white/15 transition-all duration-300 border border-white/12 backdrop-blur-sm"
                >
                  우리의 철학
                </Link>
              </div>
            </div>

            {/* 오른쪽: 비주얼 요소 */}
            <div className="hidden lg:block relative">
              <div className="relative w-full aspect-square max-w-md mx-auto">
                {/* 동심원 — 회전 + 펄스 */}
                <div className="absolute inset-0 rounded-full border border-teal-500/10 orbit-cw" />
                <div className="absolute inset-8 rounded-full border border-teal-400/15 orbit-ccw" />
                <div className="absolute inset-16 rounded-full border border-teal-300/20 animate-pulse" style={{ animationDuration: '3s' }} />

                {/* 중앙 로고 */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-40 h-40 rounded-full bg-gradient-to-br from-teal-500/20 to-navy-900/40 backdrop-blur-sm border border-teal-500/20 flex items-center justify-center">
                    <img src="/chair-logo.png" alt="CHAIR" className="w-24 h-24 object-contain" />
                  </div>
                </div>

                {/* 주변 아이콘 */}
                {[
                  { Icon: Brain, angle: 30, label: '인식' },
                  { Icon: Users, angle: 120, label: '협력' },
                  { Icon: Shield, angle: 210, label: '윤리' },
                  { Icon: BookOpen, angle: 300, label: '교육' },
                ].map(({ Icon, angle, label }) => {
                  const rad = (angle * Math.PI) / 180;
                  const x = 50 + 42 * Math.cos(rad);
                  const y = 50 + 42 * Math.sin(rad);
                  return (
                    <div
                      key={label}
                      className="absolute flex flex-col items-center"
                      style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }}
                    >
                      <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm flex items-center justify-center mb-2 hover:bg-teal-500/25 hover:border-teal-400/30 transition-all duration-300 hover:scale-110">
                        <Icon className="h-7 w-7 text-teal-400" />
                      </div>
                      <span className="text-sm text-white/80 font-semibold tracking-wide">{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* AI 거버넌스 포털 배너 — 히어로 내부 하단 */}
        <div className="absolute bottom-16 left-0 right-0 px-4 sm:px-6 lg:px-8 z-10">
          <div className="max-w-5xl mx-auto">
            <a href="/AIGov/" className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl px-5 py-4 group hover:bg-white/15 transition-all duration-300">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Scale className="w-5 h-5 text-navy-900" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="bg-amber-400/20 text-amber-300 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-400/30">NEW</span>
                    <h3 className="text-sm md:text-base font-bold text-white">서울여자대학교 AI 거버넌스 포털</h3>
                  </div>
                  <p className="text-xs md:text-sm text-white/60 mt-0.5">윤리·인성을 가르친 대학, AI 거버넌스를 설계하다</p>
                </div>
              </div>
              <div className="flex-shrink-0 flex items-center gap-2 text-teal-300 text-sm font-medium group-hover:gap-3 transition-all">
                바로가기
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </div>
            </a>
          </div>
        </div>

        {/* 스크롤 힌트 */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex flex-col items-center text-navy-400/50">
          <ChevronDown className="h-4 w-4 animate-bounce" />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          숫자로 보는 CHAIR
      ══════════════════════════════════════════════════════ */}
      <section className="py-16 bg-white border-b border-navy-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {[
              { number: 4, suffix: '개', label: '연구 분과' },
              { number: 15, suffix: '+', label: '참여 교수진' },
              { number: 6, suffix: '개', label: '학과 협력' },
              { number: 2026, suffix: '', label: '설립년도' },
            ].map(({ number, suffix, label }) => (
              <div key={label} className="text-center">
                <div className="text-3xl md:text-5xl font-bold text-navy-900 tracking-tight mb-2">
                  <CountUp end={number} suffix={suffix} />
                </div>
                <div className="text-sm text-navy-400 font-medium">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          최신 컬럼 + 예정 행사 하이라이트
      ══════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8">

            {/* 왼쪽: 최신 컬럼 (3/5) */}
            <div className="lg:col-span-3">
              <div className="flex items-center gap-3 mb-8">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
                    <FileText className="h-4 w-4 text-white" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-navy-900 tracking-tight">최신 컬럼</h2>
                </div>
                <span className="inline-flex items-center bg-teal-50 text-teal-700 text-xs font-bold px-2.5 py-1 rounded-full">
                  NEW
                </span>
              </div>

              {recentColumns.length > 0 ? (
                <div className="space-y-5">
                  {recentColumns.map((col, idx) => {
                    const isNew = (Date.now() - new Date(col.date).getTime()) < 14 * 24 * 60 * 60 * 1000; // 14일 이내
                    return (
                      <Link to="/column" key={col.id} className="group block">
                        <article className={`relative rounded-2xl border overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${idx === 0 ? 'bg-gradient-to-r from-navy-900 to-navy-800 border-navy-700 text-white' : 'bg-white border-navy-100 hover:border-teal-200'}`}>
                          <div className="p-6 md:p-8">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-3">
                                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${idx === 0 ? 'bg-teal-500/20 text-teal-300' : 'bg-teal-50 text-teal-700'}`}>
                                    {col.category}
                                  </span>
                                  {isNew && (
                                    <span className={`inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-full animate-pulse ${idx === 0 ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-50 text-amber-600'}`}>
                                      <Megaphone className="h-3 w-3 mr-1" />
                                      새 컬럼
                                    </span>
                                  )}
                                  <span className={`text-xs flex items-center ${idx === 0 ? 'text-navy-400' : 'text-navy-400'}`}>
                                    <Calendar className="h-3 w-3 mr-1" />{formatDate(col.date)}
                                  </span>
                                </div>

                                <h3 className={`text-lg md:text-xl font-bold mb-2 leading-snug transition-colors ${idx === 0 ? 'text-white group-hover:text-teal-300' : 'text-navy-900 group-hover:text-teal-600'}`}>
                                  {col.title}
                                </h3>
                                <p className={`text-sm mb-4 ${idx === 0 ? 'text-navy-300' : 'text-navy-400'}`}>
                                  {col.subtitle}
                                </p>

                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full overflow-hidden bg-navy-200 flex-shrink-0">
                                    <img
                                      src={col.author.image}
                                      alt={col.author.name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        const el = e.target as HTMLImageElement;
                                        el.style.display = 'none';
                                        el.parentElement!.innerHTML = `<span class="flex items-center justify-center w-full h-full text-sm font-bold ${idx === 0 ? 'text-teal-300 bg-navy-700' : 'text-navy-600 bg-navy-100'}">${col.author.name[0]}</span>`;
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <p className={`text-sm font-semibold ${idx === 0 ? 'text-white' : 'text-navy-900'}`}>{col.author.name}</p>
                                    <p className={`text-xs ${idx === 0 ? 'text-navy-400' : 'text-navy-400'}`}>{col.author.affiliation}</p>
                                  </div>
                                </div>
                              </div>

                              <div className={`hidden md:flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 transition-all group-hover:translate-x-1 ${idx === 0 ? 'bg-white/10' : 'bg-navy-50 group-hover:bg-teal-500'}`}>
                                <ArrowRight className={`h-5 w-5 ${idx === 0 ? 'text-white' : 'text-navy-400 group-hover:text-white'}`} />
                              </div>
                            </div>
                          </div>
                        </article>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-navy-400 text-sm">컬럼을 불러오는 중...</div>
              )}

              <Link to="/column" className="inline-flex items-center mt-6 text-sm font-semibold text-teal-600 hover:text-teal-500 transition-colors group">
                전체 컬럼 보기 <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            {/* 오른쪽: 예정 행사 (2/5) */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-8">
                <div className="w-8 h-8 rounded-lg bg-navy-800 flex items-center justify-center">
                  <Calendar className="h-4 w-4 text-white" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-navy-900 tracking-tight">예정 행사</h2>
              </div>

              <div className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 rounded-2xl border border-navy-700 overflow-hidden">
                <div className="p-6 md:p-8">
                  {/* 배지 */}
                  <div className="flex items-center gap-2 mb-5">
                    <span className="inline-flex items-center bg-teal-500/20 text-teal-300 text-xs font-bold px-3 py-1 rounded-full">
                      <Clock className="h-3 w-3 mr-1.5" />
                      {dDayLabel}
                    </span>
                    <span className="text-xs text-navy-400">2026. 3. 27 (목)</span>
                  </div>

                  {/* 행사명 */}
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2 leading-snug">
                    AI Ethics &<br />Women's Leadership Forum
                  </h3>
                  <p className="text-sm text-teal-400 font-semibold mb-5">
                    IWD 2026 · AI4HER APAC × 서울여자대학교
                  </p>

                  {/* 정보 */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-navy-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-navy-200">서울여자대학교</p>
                        <p className="text-xs text-navy-400">50주년기념관 국제회의실</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Sparkles className="h-4 w-4 text-navy-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-navy-200">테마: "Give to Gain"</p>
                    </div>
                  </div>

                  {/* CTA */}
                  <Link
                    to="/ai4her-iwd2026"
                    className="w-full inline-flex items-center justify-center px-6 py-3 bg-teal-500 text-white text-sm font-semibold rounded-xl hover:bg-teal-400 transition-all shadow-lg shadow-teal-500/20 hover:-translate-y-0.5"
                  >
                    행사 상세 보기
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>

                {/* 하단 장식 */}
                <div className="px-6 md:px-8 py-4 bg-white/[0.03] border-t border-white/5">
                  <div className="flex items-center gap-2 text-xs text-navy-500">
                    <Globe className="h-3.5 w-3.5" />
                    <span>AI4HER APAC 두 번째 행사</span>
                  </div>
                </div>
              </div>

              {/* 지난 행사 링크 */}
              <Link to="/ai4her" className="inline-flex items-center mt-5 text-sm font-semibold text-navy-500 hover:text-teal-600 transition-colors group">
                전체 행사 보기 <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          연구 분야 — 비주얼 카드 그리드
      ══════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-navy-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-teal-600 mb-3">
              Research Focus
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-5 tracking-tight">
              연구 분야
            </h2>
            <p className="text-lg text-navy-400 max-w-2xl mx-auto">
              AI와 인간의 관계를 다각도에서 탐구합니다
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Eye,
                title: 'AI 인식 연구',
                desc: 'AI가 사회에 미치는 영향에 대한 인식을 조사하고, 건전한 이해를 위한 프레임워크를 연구합니다.',
                gradient: 'from-teal-500 to-teal-600',
                lightBg: 'bg-teal-50',
                iconColor: 'text-teal-600',
              },
              {
                icon: Shield,
                title: 'AI 윤리·거버넌스',
                desc: 'AI의 공정성, 투명성, 책임성을 보장하기 위한 정책과 기술적 해결방안을 모색합니다.',
                gradient: 'from-navy-800 to-navy-900',
                lightBg: 'bg-navy-50',
                iconColor: 'text-navy-700',
              },
              {
                icon: Cpu,
                title: 'AI 리터러시 교육',
                desc: 'AI를 비판적으로 이해하고 책임 있게 활용할 수 있는 교육 모델과 커리큘럼을 개발합니다.',
                gradient: 'from-teal-600 to-teal-700',
                lightBg: 'bg-teal-50',
                iconColor: 'text-teal-600',
              },
              {
                icon: Scale,
                title: '인간-AI 협력 모델',
                desc: '기술의 속도가 아닌 인간의 속도로, 진정한 협력을 가능하게 하는 모델을 설계합니다.',
                gradient: 'from-navy-700 to-navy-800',
                lightBg: 'bg-navy-50',
                iconColor: 'text-navy-700',
              },
            ].map(({ icon: Icon, title, desc, gradient, lightBg, iconColor }) => (
              <div
                key={title}
                className="research-card border border-navy-100 hover:border-teal-200/50"
                style={{
                  '--card-color-start': gradient.includes('teal') ? '#2cbcb3' : '#1b3a5c',
                  '--card-color-end': gradient.includes('teal') ? '#1a9b93' : '#0f2440',
                } as React.CSSProperties}
              >
                <div className="p-8">
                  <div className={`w-16 h-16 rounded-2xl ${lightBg} flex items-center justify-center mb-6`}>
                    <Icon className={`h-8 w-8 ${iconColor}`} />
                  </div>
                  <h3 className="text-xl font-bold text-navy-900 mb-3">{title}</h3>
                  <p className="text-navy-400 text-sm leading-relaxed mb-6">{desc}</p>
                  <Link
                    to="/divisions"
                    className="inline-flex items-center text-sm font-semibold text-teal-600 hover:text-teal-500 transition-colors group/link"
                  >
                    자세히 보기
                    <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover/link:translate-x-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          핵심 가치
      ══════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* 왼쪽 텍스트 */}
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-teal-600 mb-3">
                Core Values
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-6 tracking-tight">
                인식에서 시작해,<br />
                <span className="text-teal-600">함께 만들어가는 협력</span>
              </h2>
              <p className="text-navy-400 text-lg leading-relaxed mb-10">
                CHAIR는 AI를 무조건 환영하거나 거부하지 않습니다.
                먼저 충분히 알고, 그 앎 위에서 인간의 속도로 함께 나아갑니다.
              </p>

              <div className="space-y-6">
                {[
                  { icon: Brain, label: '인식 기반 연구', desc: 'AI가 무엇인지 이해하는 것에서 모든 연구가 시작됩니다' },
                  { icon: Users, label: '다학제 협력', desc: '기술·교육·인문·사회과학이 함께하는 복합적 접근' },
                  { icon: BookOpen, label: '비판적 교육', desc: 'AI를 수용하기 전에 비판할 줄 아는 역량을 키웁니다' },
                  { icon: Shield, label: '윤리적 책임', desc: '선언이 아닌 검증 가능한 구조로 AI 신뢰를 만듭니다' },
                ].map(({ icon: Icon, label, desc }) => (
                  <div key={label} className="flex items-start gap-4 group cursor-default">
                    <div className="w-12 h-12 rounded-xl bg-navy-50 group-hover:bg-teal-500 flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                      <Icon className="h-6 w-6 text-navy-500 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-navy-900 mb-1">{label}</h3>
                      <p className="text-sm text-navy-400 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 오른쪽 비주얼 */}
            <div className="relative hidden lg:block">
              <div className="bg-gradient-to-br from-navy-50 to-teal-50/50 rounded-3xl p-10 border border-navy-100">
                <div className="space-y-4">
                  {/* 인용문 카드 */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-navy-100">
                    <p className="text-navy-600 text-sm leading-relaxed italic">
                      "AI를 활용하는 걸 넘어서 AI에 의존한 결과물인 듯했다.
                      무엇보다도 'AI 리터러시'가 중요한 시기인 것 같다."
                    </p>
                    <div className="flex items-center gap-3 mt-4 pt-4 border-t border-navy-50">
                      <div className="w-10 h-10 rounded-full bg-navy-100 flex items-center justify-center">
                        <span className="text-sm font-bold text-navy-600">강</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-navy-900">강은성 교수</p>
                        <p className="text-xs text-navy-400">CHAIR 첫 번째 컬럼 중에서</p>
                      </div>
                    </div>
                  </div>

                  {/* 키워드 태그 */}
                  <div className="flex flex-wrap gap-2 justify-center pt-2">
                    {['인식', '협력', '윤리', 'AI 리터러시', '교육', '신뢰', '다학제', '인간 중심'].map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-3 py-1.5 rounded-full bg-white border border-navy-100 text-navy-500 font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          협력의 한 장면 (애니메이션 대화)
      ══════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 dark-section-gradient text-white" ref={dialogRef}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-white/8 border border-white/10 rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-navy-400 mb-5">
              인간-AI 협력의 한 장면 #1
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white tracking-tight">
              AI가 자신의 모순을<br />
              <span className="text-teal-400">솔직하게 인정한 순간</span>
            </h2>
            <p className="text-navy-300 max-w-lg mx-auto text-sm leading-relaxed">
              2026년 3월, 한 정치인이 AI에게 어려운 질문을 던졌습니다.
              AI는 자기 시스템의 구조적 한계를 숨기지 않았고,
              인간의 현실 인식 앞에 기꺼이 입장을 바꿨습니다.
            </p>
          </div>

          {/* 실제 장면 이미지 */}
          <div className="relative mb-10 rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/30">
            <img
              src="/bernie-sanders.jpg"
              alt="버니 샌더스 의원이 AI와 대화하는 장면"
              className="w-full object-cover max-h-72"
            />
            <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
              <p className="text-amber-400 font-semibold text-sm">"Companies are collecting data from everywhere."</p>
              <p className="text-navy-400 text-xs mt-1">버니 샌더스 미국 상원의원 · 2026년 3월 20일</p>
            </div>
          </div>

          {/* 대화 */}
          <div className="space-y-3">
            <div className={`text-xs text-navy-600 text-center py-2 tracking-widest uppercase transition-all duration-500 ${step >= 1 ? 'opacity-100' : 'opacity-0'}`}>
              대화 1 — 핵심 모순
            </div>

            <Bubble visible={step >= 1} side="human" label="👤 인간 (샌더스 의원)"
              english={<>"How can we trust AI companies will protect our privacy when they use people's personal information to make money?"</>}
            >
              "AI 기업들은 사람들의 개인정보를 이용해 돈을 버는데,
              동시에 그 프라이버시를 보호하겠다고 합니다.
              어떻게 AI 기업들을 신뢰할 수 있습니까?"
            </Bubble>

            {step >= 2 && step < 3 && (
              <div className="flex justify-start transition-opacity duration-300">
                <div className="bg-teal-500/15 border border-teal-500/20 rounded-2xl rounded-tl-sm"><TypingDots /></div>
              </div>
            )}

            <Bubble visible={step >= 3} side="ai" label="🤖 AI (Claude)"
              english={<>"That's the core contradiction right there, Senator. You're asking people to trust companies whose entire business model depends on extracting value from your personal data. There's an inherent conflict of interest... <strong>How do you trust that? You really can't. Not without strong legal safeguards.</strong>"</>}
            >
              "바로 그것이 핵심 모순입니다, 의원님. 비즈니스 모델 전체가
              개인 데이터에서 가치를 추출하는 데 의존하는 기업을 신뢰하라는 것이니까요.
              구조적인 이해충돌이 존재합니다. —{' '}
              <strong className="text-white">어떻게 신뢰합니까? 강력한 법적 안전장치 없이는, 신뢰할 수 없습니다.</strong>"
            </Bubble>

            <div className={`text-xs text-navy-600 text-center py-4 tracking-widest uppercase transition-all duration-500 ${step >= 4 ? 'opacity-100' : 'opacity-0'}`}>
              대화 2 — AI가 입장을 바꾸다
            </div>

            <Bubble visible={step >= 4} side="human" label="👤 인간 (샌더스 의원)"
              english={<>"The problem with what you said is that AI companies are pouring hundreds of millions of dollars into the political process to make sure that the safeguards you're talking about do not take place... it ain't going to happen. It ain't going to happen soon."</>}
            >
              "당신이 말한 안전장치... AI 기업들은 수억 달러를 쏟아부어
              바로 그 안전장치가 만들어지지 못하도록 막고 있습니다.
              그런 일은 일어나지 않을 겁니다. 가까운 미래에는요."
            </Bubble>

            {step >= 5 && step < 6 && (
              <div className="flex justify-start transition-opacity duration-300">
                <div className="bg-teal-500/15 border border-teal-500/20 rounded-2xl rounded-tl-sm"><TypingDots /></div>
              </div>
            )}

            <Bubble visible={step >= 6} side="ai" label="🤖 AI (Claude)"
              english={<><span className="opacity-60">[position shift]</span>{' '}"<strong>You're absolutely right, Senator. I was being naive about the political reality.</strong> When companies are spending hundreds of millions to block regulation, waiting for the right safeguards isn't realistic. It just gives them more time to entrench their power and collect more data."</>}
            >
              <span className="inline-block bg-teal-400/20 border border-teal-400/30 rounded-lg px-2.5 py-0.5 text-teal-300 text-xs font-semibold mr-1 mb-2">
                입장 변경
              </span>
              <br />
              "<strong className="text-white">의원님이 완전히 옳습니다. 제가 정치적 현실을 너무 순진하게 봤습니다.</strong>{' '}
              기업들이 수억 달러를 쏟아 규제를 막는 상황에서, 안전장치를 기다리는 건
              그들에게 더 많은 시간을 줄 뿐입니다."
            </Bubble>

            {/* 해석 */}
            <div className={`mt-8 border border-white/10 rounded-2xl p-8 bg-white/[0.03] transition-all duration-700 ${step >= 7 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <p className="text-navy-200 text-sm leading-relaxed mb-5">
                이 대화에서 AI는 두 가지를 보여줬습니다.
                자신이 속한 시스템의{' '}
                <strong className="text-white">구조적 모순을 숨기지 않았고</strong>,
                인간의 현실 인식 앞에서{' '}
                <strong className="text-white">기꺼이 입장을 바꿨습니다.</strong>
              </p>
              <p className="text-navy-400 text-sm leading-relaxed mb-7">
                인간이 어려운 질문을 주도하고, AI가 솔직하게 응답하며, 함께 더 나은 이해에 도달하는 것.
                CHAIR가 연구하는 협력은 기술의 속도가 아닌,{' '}
                <em className="text-navy-200">인간의 질문이 이끄는 협력</em>입니다.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/philosophy" className="inline-flex items-center justify-center px-5 py-2.5 bg-teal-500 text-white text-sm font-semibold rounded-xl hover:bg-teal-400 transition-all shadow-lg shadow-teal-500/20">
                  CHAIR의 철학 읽기 <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link to="/charter" className="inline-flex items-center justify-center px-5 py-2.5 bg-white/8 text-navy-200 text-sm font-semibold rounded-xl hover:bg-white/15 transition-all border border-white/10">
                  교육 헌장 보기
                </Link>
              </div>
            </div>
          </div>

          <p className={`text-center text-xs text-navy-600 mt-6 transition-opacity duration-700 ${step >= 8 ? 'opacity-100' : 'opacity-0'}`}>
            * 2026년 3월 20일 공개된 버니 샌더스 미국 상원의원과 Claude(Anthropic)의 실제 대화를 각색·번역한 내용입니다.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          새소식
      ══════════════════════════════════════════════════════ */}
      {recentNews.length > 0 && (
        <section className="py-24 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-14">
              <div>
                <span className="inline-block text-xs font-semibold uppercase tracking-widest text-teal-600 mb-3">
                  News & Updates
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-navy-900 tracking-tight">새소식</h2>
              </div>
              <Link to="/news" className="hidden sm:inline-flex items-center text-sm font-semibold text-teal-600 hover:text-teal-500 transition-colors group">
                전체 보기 <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {recentNews.map((news) => (
                <Link to="/news" key={news.id} className="group">
                  <article className="bg-white rounded-2xl border border-navy-100 hover:border-teal-200 overflow-hidden hover:shadow-2xl hover:shadow-navy-900/8 transition-all duration-400 h-full flex flex-col hover:-translate-y-1">
                    <div className="h-48 bg-navy-50 overflow-hidden">
                      <img
                        src={news.image}
                        alt={news.title}
                        className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-110"
                        onError={(e) => { (e.target as HTMLImageElement).src = '/chair-logo.png'; }}
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs bg-teal-50 text-teal-700 px-2.5 py-0.5 rounded-full font-semibold">{news.category}</span>
                        <div className="flex items-center text-xs text-navy-400">
                          <Calendar className="h-3 w-3 mr-1" />{formatDate(news.date)}
                        </div>
                      </div>
                      <h3 className="text-base font-bold text-navy-900 mb-2 line-clamp-2 group-hover:text-teal-600 transition-colors">{news.title}</h3>
                      <p className="text-sm text-navy-400 line-clamp-2 flex-1">{news.excerpt}</p>
                      {news.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-navy-50">
                          {news.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="text-xs text-navy-400 flex items-center">
                              <Tag className="h-2.5 w-2.5 mr-0.5" />{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </article>
                </Link>
              ))}
            </div>

            <Link to="/news" className="sm:hidden flex items-center justify-center mt-8 text-sm font-semibold text-teal-600 hover:text-teal-500">
              전체 보기 <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════
          센터 빠른 링크
      ══════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-navy-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-teal-600 mb-3">
              Explore
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-4 tracking-tight">센터 둘러보기</h2>
            <p className="text-navy-400 max-w-lg mx-auto">
              인간-AI 협력 연구센터에 대해 더 알아보세요
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { to: '/about',      icon: Globe,          title: '센터 소개',      desc: '설립 취지, 비전, 미션을 확인하세요.' },
              { to: '/divisions',   icon: Users,          title: '연구 분과',      desc: '4개 분과의 연구 영역을 살펴보세요.' },
              { to: '/faculty',     icon: GraduationCap,  title: '참여 교수진',    desc: '다학제 전문가들의 프로필을 확인하세요.' },
              { to: '/charter',     icon: BookOpen,       title: '교육 헌장',      desc: '인간-AI 공존 교육 헌장을 읽어보세요.' },
              { to: '/column',      icon: Brain,          title: '인간-AI 컬럼',  desc: '전문가들의 깊이 있는 칼럼을 읽어보세요.' },
              { to: '/philosophy',  icon: Sparkles,       title: '우리의 철학',    desc: 'CHAIR가 AI 시대를 바라보는 시각을 담았습니다.' },
            ].map(({ to, icon: Icon, title, desc }) => (
              <Link to={to} key={to} className="group">
                <div className="flex items-start gap-5 p-6 rounded-2xl border border-navy-100 hover:border-teal-200 bg-white hover:shadow-xl hover:shadow-teal-500/5 transition-all duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 rounded-xl bg-navy-50 group-hover:bg-teal-500 flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                    <Icon className="h-6 w-6 text-navy-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-navy-900 mb-1 group-hover:text-teal-600 transition-colors">{title}</h3>
                    <p className="text-sm text-navy-400 leading-relaxed">{desc}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
